import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { MsalProvider } from '@azure/msal-react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { Link, useParams, useHistory } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import './index.css';
import { login } from '../../store/Auth';
import logo from '../../static/img/logo.svg';
import { oauthConfig } from '../../store/config';
import { acceptInvite, getBranding } from '../../store/Settings';
import SocialLogin from '../../components/SocialLogin';
import OTPVerification from '../../components/OTPVerification';

// Initialize MSAL instance for Microsoft authentication
const msalConfig = {
  auth: {
    clientId: oauthConfig['microsoft']['clientId'],
    authority: oauthConfig['microsoft']['authority'],
    redirectUri: oauthConfig['microsoft']['redirectUri'],
  },
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false }
}

const msalInstance = new PublicClientApplication(msalConfig);
gsap.registerPlugin(SplitText, ScrollTrigger);
function Login() {
  const { token, hash } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);

  const {isLogin, auth, require2FA, twoFAData, requiresPasswordSetup} = useSelector(state => state['Auth']);
  const { currentBranding } = useSelector(state => state['Settings']);

  const platformName = (currentBranding?.isCustom && currentBranding?.agencyName) ? currentBranding.agencyName : 'Sales AI';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState({ password: false });
  const togglePasswordVisibility = field => setShowPassword(prev => ({ ...prev, [field]: !prev[field] }))

  useEffect(() => { document.title = `Login | ${platformName}` }, [platformName]);
  useEffect(() => {
    // Only redirect if authenticated and NOT requiring password setup
    if (auth && !requiresPasswordSetup && history['location']['pathname'].indexOf('/invite') < 0 && history['location']['pathname'].indexOf('/magicLogin') < 0) {
      history.push('/agents');
    }
  }, [auth, requiresPasswordSetup]);

  // Handle invite - runs only once on mount
  useEffect(() => {
    const inviteKey = `invite_${token}`;
    if (history['location']['pathname'].indexOf('/invite') >= 0 && token && !sessionStorage.getItem(inviteKey)) {
      sessionStorage.setItem(inviteKey, 'true');
      dispatch(acceptInvite({ token, history }));
    }
  }, []);

  // Handle magic login - runs only once using sessionStorage to prevent double dispatch
  useEffect(() => {
    const isMagicLoginPath = history['location']['pathname'].indexOf('/magicLogin') >= 0;
    const magicLoginKey = `magic_login_${hash}`;

    if (isMagicLoginPath && hash && !sessionStorage.getItem(magicLoginKey)) {
      sessionStorage.setItem(magicLoginKey, 'true');
      dispatch({ type: 'MAGIC_LOGIN_REQUEST', payload: { hash, history } });
    }
  }, []);
  
  // Handle 2FA requirement
  useEffect(() => {
    if (require2FA && twoFAData) {
      setShowOTP(true);
    }
  }, [require2FA, twoFAData]);

  const handleLogin = () => {
    dispatch(login({ data: { email, password }, history }));
  }
  
  const handleBackToLogin = () => {
    setShowOTP(false);
    dispatch({ type: 'CLEAR_2FA_STATE' });
    setPassword('');
  }

  useEffect(() => {
    // Reset any previous SplitText instances
    splitParagraphs.current.forEach((split) => split?.revert?.());
    splitParagraphs.current = [];

    // Animate each heading
    headingRefs.current.forEach((el, i) => {
      gsap.fromTo(
        el,
        { opacity: 0, filter: "blur(10px)", y: -20 },
        { opacity: 1, filter: "blur(0px)", y: 0, duration: 0.8, ease: "power3.out", delay: i * 0.3 }
      );
    });

    // Animate each paragraph with SplitText
    paragraphRefs.current.forEach((el, i) => {
      const split = new SplitText(el, { type: "chars" });
      splitParagraphs.current.push(split);

      gsap.fromTo(
        split.chars,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 1, stagger: 0.02, ease: "power2.out", delay: (headingRefs.current.length + i) * 0.1 }
      );
    });
  }, []);

  return (
    <div className='sa-auth-page'>
      {/* ── Left Hero Background ── */}
      <div className='sa-hero-bg'>
        <div className='sa-mesh-overlay'></div>
        <div className='sa-grid-lines'></div>
        <div className='sa-particle sa-particle-1'></div>
        <div className='sa-particle sa-particle-2'></div>
        <div className='sa-particle sa-particle-3'></div>
        <div className='sa-particle sa-particle-4'></div>
        <div className='sa-particle sa-particle-5'></div>
        <div className='sa-particle sa-particle-6'></div>
        <div className='sa-particle sa-particle-7'></div>
        <div className='sa-particle sa-particle-8'></div>
        <div className='sa-shape sa-shape-ring'></div>
        <div className='sa-shape sa-shape-hex'></div>
        <div className='sa-shape sa-shape-tri'></div>
      </div>

      <div className='sa-layout'>
        {/* ── Left Content ── */}
        <div className='sa-left-content'>
          <div className='sa-brand'>
            <div className='sa-brand-logo'>
              {currentBranding?.['isCustom'] && currentBranding?.['logo'] ? (
                <img src={currentBranding['logo']} alt={currentBranding['agencyName'] || 'Logo'} style={{ height: '42px', maxWidth: '160px', objectFit: 'contain' }} />
              ) : (
                <svg width="42" height="42" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="lO1" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#e8709a" stopOpacity="0"/><stop offset="40%" stopColor="#ff9cc0"/><stop offset="60%" stopColor="#ff9cc0"/><stop offset="100%" stopColor="#e8709a" stopOpacity="0"/></linearGradient>
                    <linearGradient id="lO2" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#c44070" stopOpacity="0"/><stop offset="40%" stopColor="#e8709a"/><stop offset="60%" stopColor="#e8709a"/><stop offset="100%" stopColor="#c44070" stopOpacity="0"/></linearGradient>
                    <radialGradient id="lN" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#ffffff"/><stop offset="50%" stopColor="#ffb8d0"/><stop offset="100%" stopColor="#d4547a"/></radialGradient>
                    <filter id="lG" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                  </defs>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(-40 27 27)" fill="none" stroke="url(#lO1)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(40 27 27)" fill="none" stroke="url(#lO2)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(90 27 27)" fill="none" stroke="url(#lO1)" strokeWidth="1.2" opacity="0.7"/>
                  <circle cx="8.5" cy="27" r="3" fill="#ff9cc0" filter="url(#lG)"/>
                  <circle cx="44.5" cy="22" r="2.4" fill="#e8709a" filter="url(#lG)"/>
                  <circle cx="30" cy="8.5" r="2.4" fill="#c44070" filter="url(#lG)"/>
                  <circle cx="27" cy="27" r="6.5" fill="rgba(84,21,48,0.5)" filter="url(#lG)"/>
                  <circle cx="27" cy="27" r="5" fill="url(#lN)"/>
                  <circle cx="27" cy="27" r="2.5" fill="white" opacity="0.95"/>
                </svg>
              )}
            </div>
            <span className='sa-brand-name'>{platformName}</span>
          </div>
          <div className='sa-hero-badge'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            AI-Powered Sales Platform
          </div>
          <h1 className='sa-hero-headline'>Close deals<br />smarter,<br />not harder.</h1>
          <p className='sa-hero-subtitle'>Access predictive AI insights, automated follow-ups and always-on AI assistance.</p>

          <div className='sa-stats-grid'>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>3.2x</span>
              <span className='sa-stat-label'>Conversion</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>12k+</span>
              <span className='sa-stat-label'>Active Sellers</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>89%</span>
              <span className='sa-stat-label'>Time Saved</span>
            </div>
          </div>

          <div className='sa-testimonial'>
            <p className='sa-testimonial-text'>"{platformName} transformed our pipeline. We close 3x more deals with half the manual work. The AI follow-ups are a game-changer."</p>
            <div className='sa-testimonial-author'>
              <div className='sa-testimonial-avatar'>SK</div>
              <div>
                <strong>Sarah Kim</strong>
                <span>VP Sales, TechCorp</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Content ── */}
        <div className='sa-right-content'>
          <div className='sa-auth-card'>
            {showOTP && twoFAData ? (
              <OTPVerification
                history={history}
                email={twoFAData.email}
                userId={twoFAData.userId}
                onBack={handleBackToLogin}
              />
            ) : (
              <>
                <div className='sa-card-icon'>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--sa-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                </div>
                <h2 className='sa-card-title'>Welcome Back</h2>
                <p className='sa-card-subtitle'>Sign in to continue to your account</p>

                <>
                  <div className='sa-social-login-wrapper'>
                    <GoogleOAuthProvider clientId={oauthConfig['google']['clientId']}>
                      <MsalProvider instance={msalInstance}>
                        <SocialLogin history={history} type='Login' />
                      </MsalProvider>
                    </GoogleOAuthProvider>
                  </div>

                  <div className='sa-divider'>
                    <span>OR</span>
                  </div>
                </>

                <ValidatorForm className='sa-form' onSubmit={handleLogin}>
                  <div className='sa-field'>
                    <label className='sa-label'>Email</label>
                    <TextValidator
                      className='sa-input'
                      type='text'
                      name='email'
                      value={email}
                      margin='dense'
                      variant='outlined'
                      placeholder='you@company.com'
                      validators={['required', 'isEmail']}
                      onChange={(e) => setEmail(e['target']['value'])}
                      errorMessages={['Email can not be empty', 'Email is not valid']}
                    />
                  </div>
                  <div className='sa-field sa-field-password'>
                    <label className='sa-label'>Password</label>
                    <TextValidator
                      margin='dense'
                      name='password'
                      value={password}
                      variant='outlined'
                      validators={['required']}
                      placeholder='Enter your password'
                      className='sa-input'
                      type={showPassword['password'] ? 'text' : 'password'}
                      errorMessages={['Password can not be empty']}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button type='button' className='sa-toggle-password' onClick={() => togglePasswordVisibility('password')}>
                      <i className={`fa ${showPassword['password'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <div className='sa-forgot-link'>
                    <Link to='/forgot-password'>Forgot Password?</Link>
                  </div>
                  <button type='submit' className='sa-btn-primary' disabled={isLogin}>
                    {!isLogin ? (
                      <>
                        Sign In
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </>
                    ) : (
                      <i className='fa fa-spinner fa-spin'></i>
                    )}
                  </button>
                </ValidatorForm>

                <p className='sa-switch-text'>
                  Don't have an account? <Link to='/signup'>Sign up</Link>
                </p>

                <div className='sa-trusted'>
                  <span className='sa-trusted-label'>Trusted by teams at</span>
                  <div className='sa-trusted-logos'>
                    <span className='sa-trusted-logo'>Stripe</span>
                    <span className='sa-trusted-logo'>Notion</span>
                    <span className='sa-trusted-logo'>Linear</span>
                    <span className='sa-trusted-logo'>Vercel</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

    /* ── OLD UI (commented out) ──
    <div className='login-page'>
      <div className='logo-area'>
        <></>
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area login-page-img'>
              <img src={require('../../static/img/login-page.png')} alt='' />
              <h2>Sales AI</h2>
              <p>Access predictive AI insights, automated follow-ups<br />and always-on AI assistance.</p>
            </div>
          </div>
          <div className='col-lg-5 offset-lg-1 col-md-6 col-sm-12 login-area'>
            <div className='login-form'>
              {showOTP && twoFAData ? (
                <OTPVerification history={history} email={twoFAData.email} userId={twoFAData.userId} onBack={handleBackToLogin} />
              ) : (
                <>
              <div className='title-area'>
                <h2>Welcome Back</h2>
                <p>Sign in to continue to your account</p>
              </div>
              {!currentBranding?.['isCustom'] && (
                <>
                  <GoogleOAuthProvider clientId={oauthConfig['google']['clientId']}>
                    <MsalProvider instance={msalInstance}>
                      <SocialLogin history={history} type='Login' />
                    </MsalProvider>
                  </GoogleOAuthProvider>
                  <div className='or-text'><p>OR</p></div>
                </>
              )}
              <ValidatorForm className='validator-form mt-2' onSubmit={handleLogin}>
                <div className='group-form'>
                  <label>Email</label>
                  <TextValidator className='login-input-fields' type='text' name='email' value={email} margin='dense' variant='outlined' placeholder='Email here' validators={['required', 'isEmail']} onChange={(e) => setEmail(e['target']['value'])} errorMessages={['Email can not be empty', 'Email is not valid']} />
                </div>
                <div className='group-form password-group'>
                  <label>Password</label>
                  <TextValidator margin='dense' name='password' value={password} variant='outlined' validators={['required']} placeholder='Password here' className='login-input-fields' type={showPassword['password'] ? 'text' : 'password'} errorMessages={['Password can not be empty']} onChange={(e) => setPassword(e.target.value)} />
                  <button type='button' className='toggle-password-visibility' onClick={() => togglePasswordVisibility('password')}>
                    <i className={`fa ${showPassword['password'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className='group-form text-center mt-4'>
                  <button type='submit' className='btn-style-new' disabled={isLogin}>
                    {!isLogin ? 'Sign In' : <div><i className='fa fa-spinner fa-spin'></i></div>}
                  </button>
                </div>
              </ValidatorForm>
              <div className='group-form checkbox-group'>
                <div className='group-form checkbox-groups'>
                  Don't have an account ? <Link className='forget-pass' to='/signup'> &nbsp; Sign up</Link>
                </div>
                <Link className='forget-pass' to='/forgot-password'>Forgot Password ?</Link>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    */
  )
}

export default Login;